import type { BuiltInTool } from './types'

/**
 * The whole `memory.*` surface. The run's memory dispatcher switches exhaustively over this union and the intercept
 * refuses anything outside it; the registry below is typed by it, so neither can gain a name the other lacks.
 */
export const MEMORY_TOOL_NAMES = ['memory.ask', 'memory.train'] as const

export type MemoryToolName = (typeof MEMORY_TOOL_NAMES)[number]

/**
 * The knowledge kinds `memory.train` accepts (#689), served as its `kind` enum and read by both entry points'
 * payload mapper, so the schema cannot offer a kind the mapper would not keep. `rating` is deliberately absent: a
 * rating is feedback about another entry, written only by rating it. Any other kind is read as `fact`.
 */
export const MEMORY_TRAIN_KINDS = ['fact', 'inference', 'preference', 'correction'] as const

/**
 * Which notes an Ask may answer from (#601), served as `memory.ask`'s `selection` enum and read by every entry point,
 * so the schema cannot offer a value the API would refuse. `any` is every note Ask reads, and the default. `trained`
 * is only what somebody taught, never a lesson a run retained after its work. `confirmed` is only the owner's own
 * word: a taught note a person wrote, or one a decision at a gate wrote. Anything else is refused, never read as `any`.
 */
export const MEMORY_ASK_SELECTIONS = ['any', 'trained', 'confirmed'] as const

export type MemoryAskSelection = (typeof MEMORY_ASK_SELECTIONS)[number]

/** The `memory.*` definitions, keyed by name so a missing one is a type error. */
export const MEMORY_TOOLS: { readonly [Name in MemoryToolName]: BuiltInTool & { name: Name } } = {
    'memory.ask': {
        name: 'memory.ask',
        family: 'memory',
        summary: 'Ask enterprise memory a question, and get an answer with its evidence.',
        description:
            "Ask enterprise memory a question and get an answer with the evidence behind it. Pass purpose (what you need it for) and, to restrict the search, teamId or projectId — an explicit filter is honoured as given and never widened, so omit both to search the whole enterprise. Pass selection to say which notes may answer: any (the default), trained (only what somebody taught, never what a run retained after its work) or confirmed (only the owner's own word: a note a person taught, or a decision they made at a gate). Before asking a person something they may already have decided, ask with selection confirmed and a purpose that does not quote the answer: insufficient_evidence means nothing confirmed matched, so you may ask. Returns answer, evidence, appliedFilters, appliedSelection, status and usage. Each evidence row carries its origin, kind, authorKind, sourceEventId and its layer and layerId, which say what it covers. A status of unavailable means memory could not be asked: carry on without it, and never read it as memory knowing nothing. In a run the reply always fits one result: evidence holds the notes that fit, whole; a first note too long on its own is cut and marked partial, with chars as its full length; and omitted names each note that matched and is not there. answer names the notes cited, and their text is in evidence only. Pass limit to get at most that many notes. Asking never stores anything.",
        inputSchema: {
            type: 'object',
            properties: {
                purpose: { type: 'string', description: 'What you need the answer for. Shown in the answer.' },
                teamId: { type: 'string', description: 'Restrict to this team. Never widened back out.' },
                projectId: { type: 'string', description: 'Restrict to this project. Never widened back out.' },
                limit: {
                    type: 'integer',
                    minimum: 1,
                    maximum: 20,
                    description: 'The most notes to return. Defaults to 20.',
                },
                selection: {
                    type: 'string',
                    enum: [...MEMORY_ASK_SELECTIONS],
                    description:
                        "Which notes may answer. Defaults to any. trained leaves out what runs retained after their work; confirmed keeps only the owner's own word. Never widened.",
                },
            },
            required: [],
            additionalProperties: false,
        },
        returns:
            '`{ answer, evidence, appliedFilters, appliedSelection, status, usage }`. `status` is `answered`, `insufficient_evidence` (nothing in scope, or nothing the selection allows), `conflict` (notes disagree) or `unavailable` (memory could not be asked: no answer and no evidence, and not the same as knowing nothing). `appliedSelection` is the selection the answer was drawn under: `any` when none was named. A run reads a fixed set of layers (task, team and enterprise): a filter narrows within them and never adds one. A run also gets `omitted`: `[{ id, layer, sourceEventId, chars }]` for each note that matched and did not fit in one result, and `usage.evidenceOmitted`. Over HTTP nothing is left out, and `answer` carries the text of every note.',
        callers: ['run', 'api', 'mcp'],
        effect: 'read',
        retry: 'safe',
        retryNote: 'Asking never stores anything.',
        errors: [],
        api: { method: 'GET', path: '/v1/memory/ask', scope: 'memory:read' },
        examples: [
            { input: { purpose: 'How has this task handled flaky checks before?' } },
            { input: { purpose: 'What does the trial include?', limit: 3 }, note: 'at most three notes' },
            {
                input: { purpose: 'prior owner decisions about refunds over the limit', selection: 'confirmed' },
                note: "only the owner's own word",
            },
        ],
        public: true,
    },
    'memory.train': {
        name: 'memory.train',
        family: 'memory',
        summary: 'Teach enterprise memory one note, at the task layer by default.',
        description:
            "Teach enterprise memory one note. Pass body, optionally layer (task, the default; member and role are accepted; team and enterprise only when this task version grants that layer and the source event id starts with the granted prefix, on a cloned task too, unless a team the running member sits on or leads has withdrawn that layer, else clone_memory_not_granted), source ({eventId, revision}) naming the event you learned it from, so retrying it is not stored twice, and kind (fact, the default; inference, preference or correction). To correct a note, pass kind correction and supersedes ({entryId}) naming the entry it replaces: that retires the old note when it is in the layer you are writing, and a team or enterprise note only when this version grants that layer for the note's event id. Returns status (accepted, retained, consolidating or failed), the stored entry and usage. Author and enterprise come from this run, never from you.",
        inputSchema: {
            type: 'object',
            properties: {
                body: { type: 'string', description: 'The note to retain.' },
                layer: {
                    type: 'string',
                    enum: ['task', 'member', 'role', 'team', 'enterprise'],
                    description: 'Defaults to task. team and enterprise need a grant on this task version.',
                },
                source: {
                    type: 'object',
                    properties: { eventId: { type: 'string' }, revision: { type: 'string' } },
                    description: 'The source event this came from. Retrying the same pair is not stored twice.',
                    additionalProperties: true,
                },
                kind: {
                    type: 'string',
                    enum: [...MEMORY_TRAIN_KINDS],
                    description:
                        'What kind of note this is. Defaults to fact, and a kind outside this list is stored as a fact. A correction replaces the note named in supersedes.',
                },
                supersedes: {
                    type: 'object',
                    properties: {
                        entryId: { type: 'string', description: 'The id of the entry this note corrects.' },
                    },
                    required: ['entryId'],
                    description:
                        "The entry this note corrects. With kind correction it retires that entry: memory answers from the correction, and the old note stays only for audit. A run retires an entry in the layer it is writing. A team or enterprise note is retired only when this version grants that layer for the note's event id; one it cannot retire stays live, and the pair is reported as a conflict. Without kind correction the entry id is only recorded.",
                    additionalProperties: true,
                },
            },
            required: ['body'],
            additionalProperties: true,
        },
        returns:
            '`{ status, entry, usage }`. `status` is `accepted`, `retained`, `consolidating` or `failed`. Author and enterprise come from the run.',
        callers: ['run', 'api'],
        effect: 'write',
        retry: 'idempotent',
        retryNote:
            'Keyed on `source` (`eventId`, `revision`), or on the tool call when there is no source: a repeat is not stored twice.',
        needs: {
            grant: "`task` is always allowed. `member` and `role` only on an original task, never a clone. `team` and `enterprise` only when this version grants that layer and the source event id starts with the granted prefix. A cloned task may too, in its own enterprise's memory and never its publisher's, as a task the enterprise wrote itself may; only a layer withdrawn on the team's page of a team the running member sits on or leads stops it, with `clone_memory_not_granted`. Reserved event ids (`team:` and `gate:`) stay refused.",
        },
        errors: [
            {
                code: 'clone_cannot_train_up',
                when: 'A cloned task asked for the `member` or `role` layer.',
                handling: 'fail',
            },
            {
                code: 'clone_memory_not_granted',
                when: "A cloned task asked for `team` or `enterprise` and one of the running member's teams has withdrawn that layer on its page.",
                handling: 'fail',
            },
            {
                code: 'grant_denied',
                when: 'The call asked for `team` or `enterprise` and this version does not grant that layer for the event id.',
                handling: 'fail',
            },
        ],
        api: { method: 'POST', path: '/v1/tasks/:taskId/memory/train', scope: 'tasks:write' },
        examples: [
            {
                input: {
                    body: 'The release check needs the preview URL before it can start.',
                    source: { eventId: 'evt_1', revision: '1' },
                },
            },
        ],
        public: true,
    },
}
